/**
 * How a person is named in this product.
 *
 * Memberships and sessions carry a user id. That id is a UUID. Showing it as
 * the primary label looks like a name and is not one. Identity stores email as
 * an HMAC digest, so the BFF cannot reverse it; what it *can* return is
 * `users.identity_subject`, which for the provisioned operator is
 * `operator:<email>`. This helper turns that subject — or a session email —
 * into cells the team list and the user menu can render without inventing a
 * person.
 */

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export interface IdentityLabelInput {
  userId: string;
  identitySubject?: string | null;
  email?: string | null;
  name?: string | null;
}

export interface IdentityLabels {
  /** Empty when the API did not give a display name. Never a UUID. */
  name: string;
  /** Empty when the API did not give an email. Never a UUID. */
  email: string;
}

function usable(value: string, userId: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === userId || UUID.test(trimmed)) return "";
  return trimmed;
}

function emailFromSubject(subject: string, userId: string): string {
  const candidate = subject.includes(":") ? subject.slice(subject.indexOf(":") + 1) : subject;
  return candidate.includes("@") ? usable(candidate, userId) : "";
}

export function identityLabels(input: IdentityLabelInput): IdentityLabels {
  const name = usable(input.name ?? "", input.userId);
  const directEmail = usable(input.email ?? "", input.userId);
  const subject = usable(input.identitySubject ?? "", input.userId);
  const email = directEmail || emailFromSubject(subject, input.userId);
  const subjectName = email ? "" : subject.includes("@") ? "" : subject;
  return { name: name || subjectName, email };
}

/** Sidebar / menu primary label. Never the raw user UUID. */
export function accountLabel(input: IdentityLabelInput): string {
  const { name, email } = identityLabels(input);
  return name || email || "Account";
}
